<script setup>
import { onBeforeUnmount, ref, watch, nextTick } from "vue";
import { usePage } from "@inertiajs/vue3";
import { useAnalyticsStore } from "@/Stores/AnalyticsStore";

const props = defineProps({
	isActive: {
		type: Boolean,
		default: false
	}
});

const analyticsStore = useAnalyticsStore();
const adContainer = ref(null);
const adLoaded = ref(false);
const page = usePage();

// Unique suffix per rendered ad so multiple in-feed slots don't reuse the same
// container id (e.g. tsyndicate's fixed `containerId`), which would break every
// slot after the first.
let instanceCounter = 0;

/**
 * Give the pasted ad markup a unique container id. Finds the first element with an
 * id and rewrites every occurrence of that id in the markup (both the container
 * element and any inline script that references it via `containerId`).
 */
const uniquifyContainerId = (html) => {
	const template = document.createElement('template');
	template.innerHTML = html;

	const withId = template.content.querySelector('[id]');
	if (!withId) {
		return html;
	}

	const originalId = withId.getAttribute('id');
	const uniqueId = `${originalId}_${instanceCounter++}_${Math.floor(Math.random() * 1e6)}`;

	return html.split(originalId).join(uniqueId);
};

/**
 * Inject markup and execute any inline/external scripts it contains.
 * innerHTML does not run <script> tags, so they are recreated as live nodes.
 */
const injectAd = (html) => {
	const container = adContainer.value;
	if (!container) {
		return;
	}

	container.innerHTML = html;

	container.querySelectorAll('script').forEach((oldScript) => {
		const script = document.createElement('script');
		for (const { name, value } of oldScript.attributes) {
			script.setAttribute(name, value);
		}
		script.text = oldScript.textContent;
		oldScript.replaceWith(script);
	});
};

const loadAd = async () => {
	const html = page.props.settings?.custom_feed_ad_html;
	if (adLoaded.value || !adContainer.value || !html) {
		return;
	}
	adLoaded.value = true;

	injectAd(uniquifyContainerId(html));
	await nextTick();

	analyticsStore.sendAnalyticsEvent('ad_impression', {
		adType: 'custom-script'
	});
};

const cleanupAd = () => {
	if (adContainer.value) {
		adContainer.value.innerHTML = '';
	}
	adLoaded.value = false;
};

watch(() => props.isActive, (active) => {
	if (active) {
		loadAd();
	} else {
		cleanupAd();
	}
}, { immediate: true });

onBeforeUnmount(() => {
	cleanupAd();
});
</script>

<template>
	<div class="absolute inset-0 flex items-center justify-center overflow-hidden bg-black">
		<div ref="adContainer" class="w-full h-full"></div>
	</div>
</template>
